'use client';

import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';

interface Customer {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  email_verified_at?: string;
  phone_verified_at?: string | null;
  newsletter_subscribed: boolean;
  account_status: string;
  [key: string]: unknown;
}

interface CustomerAuthContextValue {
  customer: Customer | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  error: string | null;
  login: (email: string, password: string, remember?: boolean, language?: string, captchaToken?: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshCustomer: () => Promise<void>;
}

const CustomerAuthContext = createContext<CustomerAuthContextValue | undefined>(undefined);

interface CustomerAuthProviderProps {
  children: React.ReactNode;
  initialCustomer?: Customer | null;
  /** Sunucuda laravel_session çerezi yoksa true. Client /api/customer/me çağırmaz. */
  noSessionOnServer?: boolean;
}

class CustomerCache {
  private data: Customer | null = null;
  private timestamp: number = 0;
  private static readonly CACHE_DURATION = 5 * 60 * 1000;

  get(): Customer | null {
    if (this.data && Date.now() - this.timestamp < CustomerCache.CACHE_DURATION) {
      return this.data;
    }
    return null;
  }

  set(customer: Customer | null): void {
    this.data = customer;
    this.timestamp = Date.now();
  }

  clear(): void {
    this.data = null;
    this.timestamp = 0;
  }
}

class CustomerFetcher {
  private static pendingRequest: Promise<Customer | null> | null = null;
  private cache: CustomerCache;

  constructor(cache: CustomerCache) {
    this.cache = cache;
  }

  async fetch(signal?: AbortSignal, forceRefresh = false): Promise<Customer | null> {
    if (!forceRefresh) {
      const cached = this.cache.get();
      if (cached) return cached;

      if (CustomerFetcher.pendingRequest) {
        return CustomerFetcher.pendingRequest;
    }
    }

    const requestPromise = this.executeFetch(signal, forceRefresh);
    CustomerFetcher.pendingRequest = requestPromise;
    
    try {
      return await requestPromise;
    } finally {
      CustomerFetcher.pendingRequest = null;
    }
  }

  private async executeFetch(signal?: AbortSignal, forceRefresh = false): Promise<Customer | null> {
      try {
        const { AuthenticationService } = await import('@/services/AuthenticationService');
        const response = await AuthenticationService.getMe(forceRefresh);

        if (!response.success) {
        this.cache.set(null);
          return null;
        }

        if (response.data?.customer) {
          const customerData = response.data.customer as Customer;
        this.cache.set(customerData);
          return customerData;
        }

        return null;
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') {
          throw err;
        }
        return null;
    }
  }
}

const globalCache = new CustomerCache();
const globalFetcher = new CustomerFetcher(globalCache);

export function CustomerAuthProvider({ children, initialCustomer = null, noSessionOnServer = false }: CustomerAuthProviderProps) {
  const [customer, setCustomer] = useState<Customer | null>(initialCustomer);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  /** Bu sekme/session'da client-side fetch zaten yapıldıysa tekrar yapma (her sayfada istek gitmesin). */
  const hasFetchedThisSessionRef = useRef(false);

  const fetchCustomer = useCallback(async (signal?: AbortSignal, forceRefresh = false): Promise<Customer | null> => {
    return globalFetcher.fetch(signal, forceRefresh);
  }, []);

  const loadCustomer = useCallback(async (skipIfInitialized = false) => {
    if (skipIfInitialized && initialCustomer) {
      setCustomer(initialCustomer);
      globalCache.set(initialCustomer);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    try {
      const customerData = await fetchCustomer(abortControllerRef.current.signal, false);
      setCustomer(customerData);
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        return;
      }
      setError(err instanceof Error ? err.message : 'Failed to load customer');
      setCustomer(null);
    } finally {
      setIsLoading(false);
    }
  }, [fetchCustomer, initialCustomer]);

  useEffect(() => {
    if (initialCustomer) {
      setCustomer(initialCustomer);
      globalCache.set(initialCustomer);
      setIsLoading(false);
      hasFetchedThisSessionRef.current = true;
      return;
    }

    // SSR'da laravel_session yok → misafir; /api/customer/me (401) çağırma
    if (noSessionOnServer) {
      setCustomer(null);
      globalCache.set(null);
      setIsLoading(false);
      hasFetchedThisSessionRef.current = true;
      return;
    }

    if (typeof window !== 'undefined' && hasFetchedThisSessionRef.current) {
      const cached = globalCache.get();
      if (cached) {
        setCustomer(cached);
        setIsLoading(false);
        return;
      }
    }

    hasFetchedThisSessionRef.current = true;
    loadCustomer(false);

    return () => {};
  }, [loadCustomer, initialCustomer, noSessionOnServer]);

  const refreshCustomer = useCallback(async () => {
    globalCache.clear();
    
    setIsLoading(true);
    setError(null);
    
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    
    try {
      const customerData = await fetchCustomer(abortControllerRef.current.signal, true);
      setCustomer(customerData);
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        return;
      }
      setError(err instanceof Error ? err.message : 'Failed to refresh customer');
    } finally {
      setIsLoading(false);
    }
  }, [fetchCustomer]);

  const login = useCallback(async (email: string, password: string, remember = false, language?: string, captchaToken?: string) => {
    setIsLoading(true);
    setError(null);

    try {
      const { AuthenticationService } = await import('@/services/AuthenticationService');
      
      const response = await AuthenticationService.login({
        email,
        password,
        remember,
        language,
        captchaToken,
      });

      if (!response.success) {
        const { ErrorHandlerService } = await import('@/services/ErrorHandlerService');
        const errorMessage = ErrorHandlerService.extractErrorFromResponse(response, 'Login failed');
        throw new Error(errorMessage);
      }

      globalCache.clear();
      hasFetchedThisSessionRef.current = false;
      await new Promise(resolve => setTimeout(resolve, 200));
      await refreshCustomer();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Login failed';
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [refreshCustomer]);

  const logout = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const { AuthenticationService } = await import('@/services/AuthenticationService');
      
      const response = await AuthenticationService.logout();

      if (!response.success) {
        throw new Error(response.error || response.message || 'Logout failed');
      }

      globalCache.clear();
      setCustomer(null);
      
      if (typeof window !== 'undefined') {
        document.cookie.split(";").forEach((c) => {
          const cookieName = c.trim().split("=")[0];
          if (cookieName === 'laravel_session' || cookieName === 'XSRF-TOKEN' || cookieName === 'customer_name') {
            document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
          }
        });
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Logout failed';
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const value: CustomerAuthContextValue = {
    customer,
    isLoading,
    isAuthenticated: !!customer,
    error,
    login,
    logout,
    refreshCustomer,
  };

  return <CustomerAuthContext.Provider value={value}>{children}</CustomerAuthContext.Provider>;
}

export function useCustomerAuth() {
  const context = useContext(CustomerAuthContext);

  if (context === undefined) {
    const isBackendRender = typeof window === 'undefined' || 
                            (typeof window !== 'undefined' && 
                             (window as any).__GRAPES_PREVIEW__ === true);
    
    if (isBackendRender) {
      return {
        customer: null,
        isLoading: false,
        isAuthenticated: false,
        error: null,
        login: async () => {},
        logout: async () => {},
        refreshCustomer: async () => {},
      };
    }
    
    throw new Error('useCustomerAuth must be used within a CustomerAuthProvider');
  }

  return context;
}
